// Enriches a user's personal book curation (list of Google Books volume IDs
// stored in user_curations.book_ids) with display metadata -- title, author,
// cover, description -- for rendering in MyCurations.js.
//
// Caching strategy: check public.book_cache first (the shared cache already
// used by AddBookModal.js/BookContext.js, keyed on external_id === Google
// Books volume ID) before ever calling the Google Books API. This avoids
// hammering the API for books other users have already looked up, and
// avoids persisting any *new* personal data -- book_cache only ever holds
// public book metadata (title/author/cover/genre/isbn), never anything
// about the user who looked it up.
//
// Google Books API ToS: this only uses the documented Volumes API
// (https://developers.google.com/books/docs/v1/using) and surfaces the
// canonical Google Books link + "Data provided by Google Books" attribution
// alongside any enriched result, as required by their terms.
import { supabase } from '../lib/supabase';

const GOOGLE_BOOKS_VOLUME_URL = 'https://www.googleapis.com/books/v1/volumes';
export const GOOGLE_BOOKS_ATTRIBUTION = 'Data provided by Google Books';

// In-memory session cache on top of book_cache, so re-rendering the same
// curation list repeatedly in one session never re-queries anything.
const memoryCache = new Map();

const fromBookCacheRow = (row) => ({
  id: row.external_id,
  title: row.title,
  author: row.author,
  cover: row.cover,
  description: row.description || null,
  isbn: row.isbn,
  source: 'cache',
  attribution: GOOGLE_BOOKS_ATTRIBUTION,
  googleBooksUrl: `https://books.google.com/books?id=${row.external_id}`,
});

const fromGoogleVolume = (item) => {
  const info = item.volumeInfo || {};
  return {
    id: item.id,
    title: info.title || 'Untitled',
    author: (info.authors && info.authors[0]) || 'Unknown',
    cover: info.imageLinks?.thumbnail?.replace('http:', 'https:') || null,
    description: info.description || null,
    isbn: info.industryIdentifiers?.find(i => i.type === 'ISBN_13')?.identifier
      || info.industryIdentifiers?.[0]?.identifier
      || null,
    source: 'google',
    attribution: GOOGLE_BOOKS_ATTRIBUTION,
    googleBooksUrl: info.canonicalVolumeLink || item.selfLink || `https://books.google.com/books?id=${item.id}`,
  };
};

async function fetchFromBookCache(ids) {
  if (ids.length === 0) return new Map();
  const { data, error } = await supabase
    .from('book_cache')
    .select('*')
    .in('external_id', ids);
  const found = new Map();
  if (!error && data) {
    data.forEach(row => found.set(row.external_id, fromBookCacheRow(row)));
  }
  return found;
}

async function fetchFromGoogleBooks(id) {
  const apiKey = process.env.REACT_APP_GOOGLE_BOOKS_API_KEY;
  const url = `${GOOGLE_BOOKS_VOLUME_URL}/${id}${apiKey ? `?key=${apiKey}` : ''}`;
  try {
    const response = await fetch(url);
    const data = await response.json();
    if (data.error || !data.id) return null;
    return fromGoogleVolume(data);
  } catch (e) {
    console.error('Google Books lookup failed for', id, e);
    return null;
  }
}

// Best-effort background save into the shared book_cache so future lookups
// (by this user or anyone else) hit the cache instead of the API.
async function backfillCache(book) {
  if (!book || book.source !== 'google') return;
  await supabase.from('book_cache').upsert([{
    external_id: book.id,
    title: book.title,
    author: book.author,
    cover: book.cover,
    isbn: book.isbn,
  }], { onConflict: 'external_id' });
}

/**
 * Resolve display metadata for a list of Google Books volume IDs.
 * Order: in-memory session cache -> book_cache table -> Google Books API.
 * Returns an array in the same order as bookIds; entries that fail to
 * resolve anywhere are returned as a minimal placeholder rather than
 * dropped, so the UI can still show "unavailable" instead of silently
 * shrinking the list.
 */
export async function enrichBookIds(bookIds) {
  const ids = [...new Set((bookIds || []).filter(Boolean))];
  if (ids.length === 0) return [];

  const results = new Map();
  const stillNeeded = [];

  ids.forEach(id => {
    if (memoryCache.has(id)) {
      results.set(id, memoryCache.get(id));
    } else {
      stillNeeded.push(id);
    }
  });

  if (stillNeeded.length > 0) {
    const cached = await fetchFromBookCache(stillNeeded);
    cached.forEach((book, id) => {
      memoryCache.set(id, book);
      results.set(id, book);
    });
  }

  const remaining = ids.filter(id => !results.has(id));
  for (const id of remaining) {
    const book = await fetchFromGoogleBooks(id);
    if (book) {
      memoryCache.set(id, book);
      results.set(id, book);
      backfillCache(book);
    } else {
      const placeholder = {
        id,
        title: 'Unavailable',
        author: null,
        cover: null,
        description: null,
        isbn: null,
        source: 'unavailable',
        attribution: GOOGLE_BOOKS_ATTRIBUTION,
        googleBooksUrl: null,
      };
      memoryCache.set(id, placeholder);
      results.set(id, placeholder);
    }
  }

  return ids.map(id => results.get(id));
}
